import { useFetch } from '../react-query';
import { apiClient } from '../../apiHelper';

export interface BsExamAnalyticsBestFrame {
  view_code: string;
  frame_id: string;
  frame_number: number;
  quality_percentage?: number;
  quality_criteria_met?: number;
  quality_criteria_total?: number;
  confidence_score?: number;
  af_score?: number;
}

export interface BsExamAnalyticsSweepSummary {
  sweep_index: number;
  frame_count: number;
  frames_with_structures: number;
  total_detections: number;
  category_ids: number[];
}

export interface BsExamAnalyticsDetector {
  name: string;
  inputs?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

export interface BsExamAnalyticsStage {
  at?: string;
  status?: string;
  best_report_frames?: BsExamAnalyticsBestFrame[];
  preserved_existing_views?: unknown[];
  findings_sync?: Record<string, unknown>;
  sweep_input_summary?: BsExamAnalyticsSweepSummary[];
  detectors?: BsExamAnalyticsDetector[];
  presentation_result?: Record<string, unknown>;
  processing_time_ms?: number;
}

export interface BsExamAnalyticsPayload {
  study_instance_uid?: string;
  serie_instance_uid?: string;
  correlation_id?: string;
  summary?: string;
  stages?: {
    autoselect?: BsExamAnalyticsStage;
    rules_enqueue?: BsExamAnalyticsStage;
    rules_execution?: BsExamAnalyticsStage;
  };
}

export interface BsExamAnalyticsResponse {
  serie_instance_uid: string;
  study_instance_uid: string;
  updated_at: string | null;
  payload: BsExamAnalyticsPayload;
}

export function isBsExamAnalyticsEnabled(): boolean {
  const flag = (window as any).RUNTIME_ENV?.BS_EXAM_ANALYTICS;
  return flag === true || flag === 'true' || flag === '1';
}

export function useGetBsExamAnalytics(
  serieInstanceUid: string | null,
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
  } = {}
) {
  const { enabled = true, refetchInterval = false } = options;

  const fetchFn = async (): Promise<BsExamAnalyticsResponse | null> => {
    if (!serieInstanceUid) {
      return null;
    }
    try {
      const response = await apiClient.get(
        `/blind-sweep/analytics/series/${serieInstanceUid}`
      );
      return response?.data ?? null;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  return useFetch(
    fetchFn,
    serieInstanceUid ? ['bs-exam-analytics', serieInstanceUid] : null,
    {
      enabled: Boolean(serieInstanceUid) && enabled && isBsExamAnalyticsEnabled(),
      refetchInterval,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
}
